import { Component, inject, input, output, signal, type InputSignal, type OutputEmitterRef, type SimpleChanges, type WritableSignal } from '@angular/core';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';
import { AnnouncementsService } from '../../../services/announcements.service';
import { Order, type PageControl, type TableColumn } from '../../../interfaces/application';
import { finalize } from 'rxjs';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import type { Announcement } from '../../../interfaces/announcement';

@Component({
  selector: 'app-table-announcements',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    PaginatorModule,
    LottieComponent
  ],
  templateUrl: './table-announcements.html',
  styleUrl: './table-announcements.css',
})
export class TableAnnouncements {

  public emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  private readonly _announcementsService: AnnouncementsService = inject(AnnouncementsService);

  public localLoading: WritableSignal<boolean> = signal<boolean>(false);
  public loading: InputSignal<boolean> = input<boolean>(false);
  public search_term: InputSignal<string> = input<string>('');

  public view: OutputEmitterRef<Announcement> = output<Announcement>();

  public items: Announcement[] = [];

  public columns: TableColumn[] = [
    {
      label: 'Data/Hora',
      slug: 'created_at',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Assunto',
      slug: 'subject',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Descrição',
      slug: 'message',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Ações',
      slug: '',
      align: 'text-start',
      sortable: false,
    },
  ];

  public pageControl: PageControl = {
    page: 0,
    take: 10,
    itemCount: 0,
    pageCount: 0,
    orderField: 'created_at',
    order: Order.DESC
  }

  protected readonly order: typeof Order = Order;

  public ngOnChanges(changes: SimpleChanges): void {
    const {
      loading,
      search_term,
    } = changes;

    if (loading?.previousValue !== null && !loading?.currentValue) {
      this.loadData();
    }
    else if (search_term?.currentValue && search_term?.currentValue !== search_term?.previousValue) {
      this.loadData();
    }
  }

  public loadData(): void {

    const filters = {
      search_term: this.search_term() || '',
    };

    this.localLoading.set(true);
    this._announcementsService.get(this.pageControl, filters)
      .pipe(finalize(() => this.localLoading.set(false)))
      .subscribe({
        next: response => {
          this.items = response.data;

          this.pageControl.page = response.meta.current_page;
          this.pageControl.itemCount = response.meta.total;
        }
      })
  }

  public sort(slug: string): void {
    this.pageControl.page = 0;
    this.pageControl.orderField = slug;
    this.pageControl.order = this.pageControl.order === Order.ASC ? Order.DESC : Order.ASC;

    this.loadData();
  }

  public onPageChange(e: PaginatorState): void {
    this.pageControl.page = (e?.page || 0) + 1;
    this.pageControl.take = (e?.rows || 10);

    this.loadData();
  }
}
